import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import invariant from 'invariant';
import { useRef, useLayoutEffect, useCallback, useState, useEffect } from 'react';
var isClient = typeof window !== 'undefined' && typeof Document !== 'undefined' && typeof HTMLElement !== 'undefined';
export function getScrollRect(element) {
  var scrollingElement;
  if ('scrollingElement' in element && element.scrollingElement) {
    scrollingElement = element.scrollingElement;
  } else if ('body' in element) {
    scrollingElement = element.body;
  } else {
    scrollingElement = element;
  }
  invariant(scrollingElement, "The provided element ".concat(element, " is not a scrolling element!"));
  var _scrollingElement = scrollingElement,
    top = _scrollingElement.scrollTop,
    left = _scrollingElement.scrollLeft,
    width = _scrollingElement.scrollWidth,
    height = _scrollingElement.scrollHeight;
  return {
    top: top,
    left: left,
    width: width,
    height: height
  };
}
export function getNode(node) {
  if (node) {
    node = 'node' in node ? node.node : node;
    node = 'element' in node ? node.element : node;
  }
  return node;
}
export function getNearestScrollNode(node) {
  if (!isClient) return null;
  node = getNode(node);
  if (node instanceof Document) return node;
  if (!(node instanceof HTMLElement)) return null;
  var _window$getComputedSt = window.getComputedStyle(node),
    overflowX = _window$getComputedSt.overflowX,
    overflowY = _window$getComputedSt.overflowY;
  if (overflowX === 'scroll' || overflowY === 'scroll') return node;
  return getNearestScrollNode(node.parentNode) || document;
}
/**
 * `useForceUpdate` will return a function that, when called
 * will force the component to rerender.
 */

export function useForceUpdate() {
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    flipUpdateBit = _useState2[1];
  var forceUpdate = useCallback(function forceUpdate() {
    flipUpdateBit(function (v) {
      return !v;
    });
  }, []);
  return forceUpdate;
}
/**
 * `useNearestScrollNodeRef` is a React hook for finding
 * the nearest scrollable ancestor of a DOM node.
 *
 * Returns a `RefObject` pointing the nearest scrollable node, or `null`.
 */

export function useNearestScrollNodeRef(
/**
 * A ref to a DOM Element.
 * Note that the node does not have to be scrollable itself,
 * as `useNearestScrollNodeRef` will traverse the DOM to find
 * a scrollable parent.
 */
ref) {
  var current = ref.current;
  var forceUpdate = useForceUpdate();
  var scrollRef = useRef(null);
  useLayoutEffect(function () {
    scrollRef.current = getNearestScrollNode(current);
    forceUpdate();
  }, [current, forceUpdate]);
  return scrollRef;
}

/**
 * `useSubscribableEvent` is a React hook for subscribing
 * and unsubscribing to DOM events using a 'subscribable' API
 * instead of the native `EventTarget` (`{add,remove}EventListener`) API.
 *
 * Returns a `Subscribe` function that is called with an `EventListener`
 * and returns an `Unsubscribe` callback.
 */
export function useSubscribableEvent(
/**
 * A ref to a DOM `EventTarget`.
 * This is the target that can emit events of the type specified by `event`.
 *
 * If the ref value is `null` (i.e., not mounted),
 * then subscribers will not receive events.
 */
ref,
/**
 * The event type to make subscribable.
 */
event) {
  var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

  // Note: we use state instead of a ref to track these values
  // because `useState` supports lazy instantiation (via a callback),
  // whereas`useRef` would have us creating and throwing away a `new Map()`
  var _useState3 = useState(function () {
      return new Map();
    }),
    _useState4 = _slicedToArray(_useState3, 1),
    subscribers = _useState4[0];
  var _useState5 = useState(function () {
      return new Map();
    }),
    _useState6 = _slicedToArray(_useState5, 1),
    listeners = _useState6[0];
  var forceUpdate = useForceUpdate();
  var listenerOptions = useRef(options);
  var capture = options.capture,
    once = options.once,
    passive = options.passive;
  useEffect(
  /**
   * `updateOptionsIfNecesssary` will update the listener options
   * if they change. This may result in a new event binding being
   * created on layout.
   */
  function updateOptionsIfNecesssary() {
    listenerOptions.current = {
      capture: capture,
      once: once,
      passive: passive
    };
    if (listeners.size > 0) {
      var _iteratorNormalCompletion = true;
      var _didIteratorError = false;
      var _iteratorError = undefined;
      try {
        for (var _iterator = listeners.values()[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
          var removeListenerIfPossible = _step.value;
          removeListenerIfPossible(true);
        }
      } catch (err) {
        _didIteratorError = true;
        _iteratorError = err;
      } finally {
        try {
          if (!_iteratorNormalCompletion && _iterator.return != null) {
            _iterator.return();
          }
        } finally {
          if (_didIteratorError) {
            throw _iteratorError;
          }
        }
      }
    }
  }, [capture, once, passive, listeners]);
  /**
   * `handleEvent` is called for every event emitted by the `EventTarget`.
   * It will pass the event to every subscriber.
   */

  var handleEvent = useCallback(function handleEvent(event) {
    if (subscribers.size) {
      var _iteratorNormalCompletion2 = true;
      var _didIteratorError2 = false;
      var _iteratorError2 = undefined;
      try {
        for (var _iterator2 = subscribers.keys()[Symbol.iterator](), _step2; !(_iteratorNormalCompletion2 = (_step2 = _iterator2.next()).done); _iteratorNormalCompletion2 = true) {
          var subscriber = _step2.value;
          subscriber(event);
        }
      } catch (err) {
        _didIteratorError2 = true;
        _iteratorError2 = err;
      } finally {
        try {
          if (!_iteratorNormalCompletion2 && _iterator2.return != null) {
            _iterator2.return();
          }
        } finally {
          if (_didIteratorError2) {
            throw _iteratorError2;
          }
        }
      }
    }
  }, [subscribers]);
  useLayoutEffect(
  /**
   * `addListenerIfNecessary` will run on layout to determine if we need to
   * start listening to events from the `EventTarget`. If we are
   * already listening, or if there are no subscribers, it will do nothing.
   */
  function addListenerIfNecessary() {
    var eventTarget = ref.current;
    var eventOptions = listenerOptions.current;
    if (eventTarget && !listeners.has(eventTarget) && subscribers.size > 0) {
      eventTarget.addEventListener(event, handleEvent, eventOptions);
      listeners.set(eventTarget,
      /**
       * `removeListenerIfPossible` will be run when a subscriber
       * unsubscribes, when the listener options change, and on unmount.
       * It will stop listening to the `EventTarget` if `force`
       * is truthy or if there are no subscribers.
       */
      function removeListenerIfPossible(force) {
        if (force || subscribers.size <= 0) {
          eventTarget.removeEventListener(event, handleEvent, eventOptions);
          listeners.delete(eventTarget);
        }
      });
    }
  });
  useEffect(function () {
    /**
     * `cleanup` will run on unmount to remove event listeners.
     */
    function cleanup() {
      if (listeners.size > 0) {
        var _iteratorNormalCompletion3 = true;
        var _didIteratorError3 = false;
        var _iteratorError3 = undefined;
        try {
          for (var _iterator3 = listeners.values()[Symbol.iterator](), _step3; !(_iteratorNormalCompletion3 = (_step3 = _iterator3.next()).done); _iteratorNormalCompletion3 = true) {
            var removeListenerIfPossible = _step3.value;
            removeListenerIfPossible(true);
          }
        } catch (err) {
          _didIteratorError3 = true;
          _iteratorError3 = err;
        } finally {
          try {
            if (!_iteratorNormalCompletion3 && _iterator3.return != null) {
              _iterator3.return();
            }
          } finally {
            if (_didIteratorError3) {
              throw _iteratorError3;
            }
          }
        }
      }
    }
    return cleanup;
  }, [listeners]);
  /**
   * `subscribe` will add the `EventListener` to the `subscribers` map,
   * if the `EventListener` isn't already subscribed.
   */

  var subscribe = useCallback(function subscribe(handler) {
    if (!subscribers.has(handler)) {
      subscribers.set(handler, true);
    }
    /**
     * `unsubscribe` will remove the subscribed `EventListener` from
     * the `subscribers` map, if the `EventListener` is still subscribed.
     */

    function unsubscribe() {
      if (subscribers.has(handler)) {
        subscribers.delete(handler);
        forceUpdate();
      }
    }
    forceUpdate();
    return unsubscribe;
  }, [subscribers, forceUpdate]);
  return subscribe;
}
var SCROLL = 'scroll';
var LISTENER_OPTIONS = {
  passive: true
};
/**
 * `useScrollEffect` will subscribe to scroll events on the given
 * `scrollRef`, passing them to the given `listener` callback.
 * A memoized version of the `listener` callback will be used
 * until one of the `inputs` has changed.
 */

export function useScrollEffect(scrollRef, listener, deps) {
  var handler = useCallback(listener, deps);
  var subscribe = useSubscribableEvent(scrollRef, SCROLL, LISTENER_OPTIONS);
  useEffect(function () {
    return subscribe(handler);
  }, [subscribe, handler]);
}